function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
import AssetComponent from "components/core/asset/asset-component";
import classnames from "classnames";
import { trackEvent } from "services/bigbrain-service";
import { TAB_CLICKED, CAROUSEL_ARROW_CLICKED } from "constants/bigbrain-event-types";
import { StyledScrollableTabs } from "./scrollable-tabs.scss";
import DefaultProps from "./default-props";
import RightArrowIcon from "./right-arrow-icon";
import LeftArrowIcon from "./left-arrow-icon";
import MondayMarkupLanguageComponent from "client/components/system/monday-markup-language/monday-markup-language-component";
import { DUPLICATE_TABS_BY } from "./scrollable-tabs-consts";
import { getElementsInsideParent } from "./scrollable-tabs-utils";
const MINIMUM_SCROLLABLE_TABS = 3;
const ScrollableTabs = (props)=>{
    const { tabs, defaultSelectedTabIndex, withDividerLine, selectedIconBackground, tabWidth, contentMarginTop, tabContentMaxWidth, iconSize, segmentType } = props;
    const [selectedTab, setSelectedTab] = useState(tabs === null || tabs === void 0 ? void 0 : tabs[defaultSelectedTabIndex]);
    const [tabsOffset, setTabsOffset] = useState(0);
    const [isSelctorLineTransition, setIsSelctorLineTransition] = useState(false);
    const [selectorLineOffset, setSelectorLineOffset] = useState(0);
    const [tabsScrollby, setTabsScrollby] = useState(0);
    const visibleTabsContainerRef = useRef(null);
    const tabsContainerRef = useRef(null);
    const duplicateTabs = [].concat(...Array(DUPLICATE_TABS_BY).fill(tabs));
    // functions that are used in useEffect:
    const jumpSelectorLineToVisibleSelectedTabIFNeeded = useCallback(()=>{
        /* this makes sure that the selector line "jumps" (moves without transition) to the last seen selected tab 
        so that when a new tab is selected the line moves over from the correct tab */ const visibleSelectedTab = getFirstVisibleSelectedTag();
        if (visibleSelectedTab === null || visibleSelectedTab === void 0 ? void 0 : visibleSelectedTab.offsetLeft) {
            moveSelectorLine({
                offsetLeft: visibleSelectedTab.offsetLeft,
                withTransition: false
            });
        }
    }, []);
    const recalculateAndSetTabsScrollby = useCallback(()=>{
        // always scroll tabs by half of the number of visible tabs, minimum 3
        const tabsContainerOuterElement = visibleTabsContainerRef.current;
        const visibleTabs = Math.floor(tabsContainerOuterElement.offsetWidth / tabWidth);
        const newScrollby = Math.max(Math.floor(visibleTabs / 2), MINIMUM_SCROLLABLE_TABS);
        setTabsScrollby(newScrollby);
    }, [
        tabWidth
    ]);
    const centerTabs = useCallback(()=>{
        const visibleTabsContainerWidth = visibleTabsContainerRef.current.offsetWidth;
        const tabsContainerWidth = tabsContainerRef.current.offsetWidth;
        const newTabsOffset = Math.floor((visibleTabsContainerWidth - tabsContainerWidth) / 2);
        setTabsOffset(newTabsOffset);
    }, []);
    const onTabsTransitionEndHandler = useCallback((e)=>{
        if (e.propertyName === "transform") {
            jumpSelectorLineToVisibleSelectedTabIFNeeded();
        }
    }, [
        jumpSelectorLineToVisibleSelectedTabIFNeeded
    ]);
    //  useEffect:
    useEffect(()=>{
        const tabsContainerElement = tabsContainerRef.current;
        window.addEventListener("resize", recalculateAndSetTabsScrollby);
        tabsContainerElement.addEventListener("transitionend", onTabsTransitionEndHandler);
        centerTabs();
        recalculateAndSetTabsScrollby();
        jumpSelectorLineToVisibleSelectedTabIFNeeded(); // move selector line to the first visible selected tab
        return ()=>{
            window.removeEventListener("resize", recalculateAndSetTabsScrollby);
            tabsContainerElement.removeEventListener("transitionend", onTabsTransitionEndHandler);
        };
    }, [
        onTabsTransitionEndHandler,
        centerTabs,
        recalculateAndSetTabsScrollby,
        jumpSelectorLineToVisibleSelectedTabIFNeeded
    ]);
    // event handlers:
    const onRightArrowClicked = ()=>{
        trackEvent(CAROUSEL_ARROW_CLICKED, {
            placement: segmentType,
            info3: "next"
        });
        setTabsOffset(tabsOffset - tabsScrollby * tabWidth);
    };
    const onLeftArrowClicked = ()=>{
        trackEvent(CAROUSEL_ARROW_CLICKED, {
            placement: segmentType,
            info3: "prev"
        });
        setTabsOffset(tabsOffset + tabsScrollby * tabWidth);
    };
    const onTabClicked = (e, tab)=>{
        trackEvent(TAB_CLICKED, {
            placement: segmentType,
            kind: tab.label
        });
        const closestTab = e.target.closest(".tab");
        if (closestTab === null || closestTab === void 0 ? void 0 : closestTab.offsetLeft) {
            moveSelectorLine({
                offsetLeft: closestTab.offsetLeft,
                withTransition: true
            });
        }
        setSelectedTab(tab);
    };
    // uitility functions:
    const moveSelectorLine = ({ offsetLeft, withTransition = true })=>{
        setIsSelctorLineTransition(withTransition);
        setSelectorLineOffset(offsetLeft);
    };
    const getFirstVisibleSelectedTag = ()=>{
        const visibleTabsContainerElement = tabsContainerRef.current;
        const selectedTabs = document.querySelectorAll(".selected");
        const visibleTab = getElementsInsideParent(selectedTabs, visibleTabsContainerElement)[0];
        return visibleTab;
    };
    return /*#__PURE__*/ _jsx(StyledScrollableTabs, {
        withDividerLine: withDividerLine,
        selectedIconBackground: selectedIconBackground,
        tabWidth: tabWidth,
        contentMarginTop: contentMarginTop,
        tabContentMaxWidth: tabContentMaxWidth,
        iconSize: iconSize,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "scrollable-tabs",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "tabs-menu",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "arrow-container left-arrow-container",
                            onClick: onLeftArrowClicked,
                            children: /*#__PURE__*/ _jsx(LeftArrowIcon, {})
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "visible-tabs-container",
                            ref: visibleTabsContainerRef,
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "tabs-container",
                                ref: tabsContainerRef,
                                style: {
                                    transform: `translateX(${tabsOffset}px)`
                                },
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "tabs",
                                        children: duplicateTabs === null || duplicateTabs === void 0 ? void 0 : duplicateTabs.map((tab, index)=>{
                                            const isSelected = (tab === null || tab === void 0 ? void 0 : tab.label) === (selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.label);
                                            return /*#__PURE__*/ _jsxs("div", {
                                                className: `tab ${isSelected ? "selected" : ""}`,
                                                onClick: (e)=>onTabClicked(e, tab),
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "tab-icon-container",
                                                        children: /*#__PURE__*/ _jsx(AssetComponent, {
                                                            image: {
                                                                src: tab === null || tab === void 0 ? void 0 : tab.iconAsset
                                                            },
                                                            className: "tab-icon"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(MondayMarkupLanguageComponent, {
                                                        className: "tab-label",
                                                        text: tab === null || tab === void 0 ? void 0 : tab.label
                                                    })
                                                ]
                                            }, index);
                                        })
                                    }),
                                    withDividerLine && /*#__PURE__*/ _jsx("div", {
                                        className: classnames("tab-selector-line", {
                                            "with-transition": isSelctorLineTransition
                                        }),
                                        style: {
                                            "--selector-line-left": `${`${selectorLineOffset}`}px`
                                        },
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "tab-selector-line-color"
                                        })
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "arrow-container right-arrow-container",
                            onClick: onRightArrowClicked,
                            children: /*#__PURE__*/ _jsx(RightArrowIcon, {})
                        })
                    ]
                }),
                withDividerLine && /*#__PURE__*/ _jsx("div", {
                    className: "tabs-divider-line"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "tab-content",
                    children: tabs === null || tabs === void 0 ? void 0 : tabs.map((tab, index)=>/*#__PURE__*/ _jsx("div", {
                            className: classnames("tab-content-container", {
                                hidden: tab !== selectedTab
                            }),
                            children: /*#__PURE__*/ _jsx(AssetComponent, _object_spread_props(_object_spread({}, tab.asset), {
                                className: "tab-content-asset"
                            }))
                        }, index))
                })
            ]
        })
    });
};
ScrollableTabs.defaultProps = DefaultProps;
export default ScrollableTabs;
